import { CheckCircle, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  AppData,
  AuthResult,
  StepInfo,
} from '../../models/applicationConnection'
import { Spinner } from '../Spinner'
import { StepWrapper } from './StepWrapper'

type ResultStepProps = {
  appData: AppData
  authResult: AuthResult
  stepInfo: StepInfo
  authError?: boolean
  namespace?:
    | 'app_connection'
    | 'app_login'
    | 'app_authentication'
    | 'password_change'
    | 'access_removal'
    | 'disconnect'
  accountName?: string
  friendlyMessage?: string | null
}

export function ResultStep({
  appData,
  authResult,
  stepInfo,
  authError,
  namespace = 'app_connection',
  accountName,
  friendlyMessage,
}: ResultStepProps) {
  const { t } = useTranslation('common')

  return (
    <StepWrapper
      appData={appData}
      stepInfo={stepInfo}
      accountName={accountName}
    >
      {authResult.loading && (
        <div className="flex flex-col items-center justify-center space-y-4 py-8 gap-10">
          <Spinner size="lg" />
          <p className="text-center text-lg text-gray-500 font-semibold">
            {t(`${namespace}.result.loading_message`)}
          </p>
        </div>
      )}

      {!authResult.loading && (
        <div className="flex flex-col items-center justify-center space-y-6 py-8">
          {authResult.success && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500" />
              <div className="text-center space-y-2">
                <h3 className="text-xl font-semibold text-green-700">
                  {t(`${namespace}.result.success.title`)}
                </h3>
                <p className="text-gray-600">
                  {t(`${namespace}.result.success.message`)}
                </p>
              </div>
            </>
          )}

          {!authResult.success && (
            <>
              <XCircle className="w-16 h-16 text-red-500" />
              <div className="text-center space-y-2">
                <h3 className="text-xl font-semibold text-red-700">
                  {t(`${namespace}.result.error.title`)}
                </h3>
                <p className="text-red-600">
                  {friendlyMessage ||
                    (authError
                      ? t(`${namespace}.result.error.auth_message`)
                      : t(`${namespace}.result.error.message`))}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </StepWrapper>
  )
}
